/**
 * Date Helper Utilities
 * Functions for date manipulation in the RSVP and Menu features
 */

import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addDays as dateFnsAddDays,
  subDays as dateFnsSubDays,
  startOfMonth as dateFnsStartOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  parse,
  isSunday as dateFnsIsSunday,
  isToday as dateFnsIsToday,
  isBefore,
  isSameDay,
  getDay,
  previousDay,
  type Day,
} from 'date-fns';

// Re-export date-fns functions we use directly
export const startOfMonth = dateFnsStartOfMonth;
export const isSunday = dateFnsIsSunday;
export const isToday = dateFnsIsToday;
export const isSameDate = isSameDay;
export const getDayOfWeek = getDay;

/**
 * Add days to a date
 * @param date - The base date
 * @param days - Number of days to add (can be negative to subtract)
 * @returns New date with days added
 */
export function addDays(date: Date, days: number): Date {
  return dateFnsAddDays(date, days);
}

/**
 * Subtract days from a date
 * @param date - The base date
 * @param days - Number of days to subtract
 * @returns New date with days subtracted
 */
export function subDays(date: Date, days: number): Date {
  return dateFnsSubDays(date, days);
}

/**
 * Get the week range (Monday to Sunday) for a given date
 * @param date - Any date within the week
 * @returns Start (Monday) and end (Sunday) of the week
 */
export function getWeekRange(date: Date): { start: Date; end: Date } {
  // weekStartsOn: 1 means Monday
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  };
}

/**
 * Get all dates in a week (Monday to Sunday)
 * @param weekStart - Start date of the week (should be a Monday)
 * @returns Array of 7 dates
 */
export function getWeekDates(weekStart: Date): Date[] {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  return eachDayOfInterval({ start: weekStart, end: weekEnd });
}

/**
 * Get the month range for a given date
 * @param date - Any date within the month
 * @returns Start and end of the month
 */
export function getMonthRange(date: Date): { start: Date; end: Date } {
  return {
    start: startOfMonth(date),
    end: endOfMonth(date),
  };
}

/**
 * Get all dates in a month
 * @param date - Any date within the month
 * @returns Array of all dates in the month
 */
export function getMonthDates(date: Date): Date[] {
  const { start, end } = getMonthRange(date);
  return eachDayOfInterval({ start, end });
}

/**
 * Navigate to next week
 */
export function nextWeek(date: Date): Date {
  return addWeeks(date, 1);
}

/**
 * Navigate to previous week
 */
export function prevWeek(date: Date): Date {
  return subWeeks(date, 1);
}

/**
 * Navigate to next month (returns first day of next month)
 */
export function nextMonth(date: Date): Date {
  return addMonths(startOfMonth(date), 1);
}

/**
 * Navigate to previous month (returns first day of previous month)
 */
export function prevMonth(date: Date): Date {
  return subMonths(startOfMonth(date), 1);
}

/**
 * Check if a date is in the past (before today)
 */
export function isPastDate(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return isBefore(date, today);
}

/**
 * Convert a Date to ISO date string (YYYY-MM-DD)
 */
export function toISODate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Parse an ISO date string (YYYY-MM-DD) to Date
 */
export function fromISODate(dateString: string): Date {
  return parse(dateString, 'yyyy-MM-dd', new Date());
}

/**
 * Format a date for display
 *
 * Common formats:
 * - 'EEE, MMM d' -> 'Mon, Jan 29'
 * - 'MMMM yyyy' -> 'January 2025'
 * - 'MMM d, yyyy' -> 'Jan 29, 2025'
 */
export function formatDate(date: Date, formatString: string): string {
  return format(date, formatString);
}

/**
 * Format a date range for display
 * @returns Formatted string like "Jan 5 - Feb 1, 2026" or "Jan 5 - 12, 2026" (same month)
 */
export function formatDateRange(start: Date, end: Date): string {
  const startMonth = format(start, 'MMM');
  const endMonth = format(end, 'MMM');
  const year = format(end, 'yyyy');

  if (startMonth === endMonth) {
    return `${format(start, 'MMM d')} - ${format(end, 'd')}, ${year}`;
  }
  return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}, ${year}`;
}

/**
 * Format a week range for display (Monday to Sunday)
 */
export function formatWeekRange(weekStart: Date): string {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  return formatDateRange(weekStart, weekEnd);
}

/**
 * Format a month for display
 * @returns Formatted string like "January 2025"
 */
export function formatMonth(date: Date): string {
  return format(date, 'MMMM yyyy');
}

/**
 * Check if RSVP is before cutoff for a given menu date
 *
 * STUB: Currently returns true for all future dates
 * TODO: Implement Tuesday 11:59 PM cutoff logic
 */
export function isBeforeCutoff(menuDate: Date): boolean {
  return !isPastDate(menuDate);
}

/**
 * Get the current week's start date (Monday)
 */
export function getCurrentWeekStart(): Date {
  return startOfWeek(new Date(), { weekStartsOn: 1 });
}

/**
 * Get the current month's first day
 */
export function getCurrentMonthStart(): Date {
  return startOfMonth(new Date());
}

/**
 * Get the cutoff date for a given week — the most recent occurrence of `cutoffDay`
 * strictly before `weekStart`. e.g. weekStart=Mon Apr 27, cutoffDay=0 (Sun) → Sun Apr 26.
 */
export function getCutoffDateForWeek(weekStart: Date, cutoffDay: number): Date {
  return previousDay(weekStart, cutoffDay as Day);
}
